import axios from "axios";
import { districtLogoUrl } from "../../../Utils/mediaUrl";

// State Agency data comes entirely from the auto-scaffolded Azure SQL REST API
// (no custom .NET endpoints). Each view returns a plain array; we filter to the
// account's allocated district set client-side, exactly like PartnerAnalyticsTab.
const DATA_API = "https://azuresql.swancreate.com/api/the";

const asArray = (resp) => (Array.isArray(resp?.data) ? resp.data : []);

const handleError = (err, context) => {
  console.error(`[StateAgencyApi] ${context}:`, err?.message || err);
  return [];
};

/** Districts allocated to a State Agency account (via allocation groups). */
export const getAllocatedDistricts = async () => {
  try {
    return asArray(await axios.get(`${DATA_API}/vw_AccountAllocatedDistricts`));
  } catch (err) {
    return handleError(err, "getAllocatedDistricts");
  }
};

/**
 * District logos, keyed by SchoolDistrictId (upper-cased — SQL Server returns
 * GUIDs upper-cased and the callers compare that way).
 *
 * vw_AccountAllocatedDistricts carries no logo column, so this is a second read
 * of SchoolDistrict joined client-side. That table is ~237 rows, small enough
 * that a join here beats changing the view and redeploying it.
 *
 * A district with no usable logo is simply absent from the map; callers fall
 * back to its initial rather than rendering a broken image.
 */
export const getDistrictLogos = async () => {
  try {
    const rows = asArray(await axios.get(`${DATA_API}/SchoolDistrict`));
    const byId = new Map();
    for (const r of rows) {
      const url = districtLogoUrl(r);
      if (url) byId.set(String(r.SchoolDistrictId || "").trim().toUpperCase(), url);
    }
    return byId;
  } catch (err) {
    handleError(err, "getDistrictLogos");
    // An empty map degrades to initials everywhere, which is a worse-looking
    // dashboard rather than a broken one.
    return new Map();
  }
};

/**
 * Accounts for the Super Admin "view as" picker: everyone who actually holds a
 * portfolio.
 *
 * Derived from vw_AccountAllocatedDistricts rather than by filtering UserMst on
 * RoleId 14 — portfolio admins keep their original role (a District Admin stays
 * roleId 2), so a role filter would hide exactly the people this picker exists
 * to impersonate. One row per user, labelled with the portfolios they oversee.
 */
export const getStateAgencies = async () => {
  try {
    const rows = asArray(await axios.get(`${DATA_API}/vw_AccountAllocatedDistricts`));
    const byUser = new Map();
    for (const r of rows) {
      const key = String(r.UserId || "").trim().toUpperCase();
      if (!key) continue;
      const cur = byUser.get(key) || { UserId: r.UserId, groups: new Set(), districts: new Set() };
      if (r.GroupName) cur.groups.add(r.GroupName);
      if (r.SchoolDistrictId) cur.districts.add(String(r.SchoolDistrictId).toUpperCase());
      byUser.set(key, cur);
    }
    if (byUser.size === 0) return [];

    // Names/emails come from the assignable-user view, which is small; fall back
    // to the group name when the holder is a dedicated Portfolio Account with no
    // district of its own (those are absent from that view).
    let directory = [];
    try {
      directory = asArray(await axios.get(`${DATA_API}/vw_PortfolioAssignableUsers`));
    } catch {
      directory = [];
    }
    const dir = new Map(directory.map((u) => [String(u.UserId || "").trim().toUpperCase(), u]));

    return [...byUser.entries()]
      .map(([key, v]) => {
        const u = dir.get(key) || {};
        const groupList = [...v.groups];
        return {
          UserId: v.UserId,
          FirstName: u.FirstName || "",
          LastName: u.LastName || "",
          Email: u.Email || "",
          RoleId: u.RoleId,
          RoleName: u.RoleName,
          GroupNames: groupList,
          districtCount: v.districts.size,
          // what the picker shows when we have no name for a dedicated account
          fallbackLabel: groupList[0] || "Portfolio Account",
        };
      })
      .sort((a, b) =>
        `${a.FirstName} ${a.LastName}`.trim().localeCompare(`${b.FirstName} ${b.LastName}`.trim())
      );
  } catch (err) {
    return handleError(err, "getStateAgencies");
  }
};

/** Per (district x academic year) metrics. */
export const getDistrictMetrics = async () => {
  try {
    return asArray(await axios.get(`${DATA_API}/vw_StateAgencyDistrictMetrics`));
  } catch (err) {
    return handleError(err, "getDistrictMetrics");
  }
};

/** Per (district x course x academic year) "courses viewed" analytics. */
export const getCourseAnalytics = async () => {
  try {
    return asArray(await axios.get(`${DATA_API}/vw_StateAgencyCourseAnalytics`));
  } catch (err) {
    return handleError(err, "getCourseAnalytics");
  }
};

/** Per (student x academic year) roster for the agency's districts. */
export const getStudents = async () => {
  try {
    return asArray(await axios.get(`${DATA_API}/vw_StateAgencyStudents`));
  } catch (err) {
    return handleError(err, "getStudents");
  }
};

/** All courses (used to surface the ones an agency has published). */
export const getAllCourses = async () => {
  try {
    return asArray(await axios.get(`${DATA_API}/CoursesMst`));
  } catch (err) {
    return handleError(err, "getAllCourses");
  }
};
